import React, { useState } from "react";

import { createVeriffFrame, MESSAGES } from "@veriff/incontext-sdk";
import { Col, Row } from "antd";

import { getVeriffUrl } from "api/kyc";
import {
  CameraSVG,
  CloseCircleSVG,
  DocumentBlurSVG,
  DocumentCutSVG,
  DocumentFlashSVG,
  DocumentGoodSVG,
  DrivingLicenseSVG,
  PassportSVG,
  TickCircleSVG,
} from "assets/icons";
import { Alert } from "components/Alert";
import { LoadingIndicator } from "components/LoadingIndicator";
import { SubmitButton } from "components/SubmitButton";
import { useMutateKyc, useStepsProgress, useUser } from "hooks";

import { reportVeriffError } from "./reportVeriffError";

import styles from "./ProofOfIdentityInit.module.scss";

const camera = {
  title: "Camera",
  description:
    "You will be asked to photograph your document and yourself. A phone, laptop or desktop camera all work.",
  icon: <CameraSVG />,
};

const essentialsByResidency = {
  us: [
    {
      title: "Passport",
      description: "The simplest option for most people.",
      icon: <PassportSVG />,
    },
    {
      title: "State driving licence",
      description: "Accepted for US residents.",
      icon: <DrivingLicenseSVG />,
    },
    camera,
  ],
  nonUs: [
    {
      title: "Passport",
      description:
        "The default choice, and the one we recommend. It works for every country and always shows place of birth.",
      icon: <PassportSVG />,
    },
    {
      title: "National ID card",
      description:
        "Works for some countries, as long as your card shows place of birth. If yours does not, use your passport.",
      icon: <DrivingLicenseSVG />,
    },
    camera,
  ],
};

const ruleByResidency = {
  us: {
    title: "What you need",
    description: "One in-date photo ID and access to a camera.",
  },
  nonUs: {
    title: "Your document must show your place of birth",
    description:
      "Our regulator requires us to record your place of birth, so your document has to show it and must be in date. Driving licences do not carry place of birth, so they cannot be used.",
  },
};

const alignments = [
  {
    title: "All good",
    icon: <TickCircleSVG className={styles.good} />,
    documentIcon: <DocumentGoodSVG />,
  },
  {
    title: "No cut",
    icon: <CloseCircleSVG />,
    documentIcon: <DocumentCutSVG />,
  },
  {
    title: "No blurry",
    icon: <CloseCircleSVG />,
    documentIcon: <DocumentBlurSVG />,
  },
  {
    title: "No flash",
    icon: <CloseCircleSVG />,
    documentIcon: <DocumentFlashSVG />,
  },
];

const Essential = ({ title, icon, description }: (typeof essentialsByResidency)["us"][number]) => (
  <div className={styles.essential}>
    <div>{icon}</div>
    <div>
      <h3>{title}</h3>
      <p>{description}</p>
    </div>
  </div>
);

const DocumentAlignment = ({ title, documentIcon, icon }: (typeof alignments)[number]) => (
  <Col className={styles.documentCell} lg={{ span: 6 }} md={{ span: 6 }} sm={{ span: 6 }} xs={{ span: 12 }}>
    <div className={styles.documentExample}>{documentIcon}</div>
    <div className={styles.documentDescription}>
      {icon}
      {title}
    </div>
  </Col>
);

export const ProofOfIdentityInit = () => {
  const [isVerifying, setIsVerifying] = useState(false);
  const { goToNextScreen, setStepsPercent, setCurrentStep } = useStepsProgress();
  const updateKyc = useMutateKyc();
  const { data: userData } = useUser();

  const isUS = userData?.isEnhanced === true;
  const essentials = isUS ? essentialsByResidency.us : essentialsByResidency.nonUs;
  const rule = isUS ? ruleByResidency.us : ruleByResidency.nonUs;

  const handleVeriffClick = () => {
    getVeriffUrl()
      .then(({ url }) => {
        setStepsPercent(15);
        setIsVerifying(true);

        try {
          createVeriffFrame({
            url,
            onEvent: (msg) => {
              switch (msg) {
                case MESSAGES.FINISHED: {
                  updateKyc
                    .mutateAsync({ currentStep: 3 })
                    .then(() => {
                      setCurrentStep(3);
                      goToNextScreen();
                    })
                    .catch((error) => {
                      setStepsPercent(5);
                      setIsVerifying(false);
                      reportVeriffError("Failed to update KYC after Veriff finished", error);
                    });
                  break;
                }
                case MESSAGES.CANCELED: {
                  setStepsPercent(5);
                  setIsVerifying(false);
                  break;
                }
              }
            },
          });
        } catch (error) {
          setStepsPercent(5);
          setIsVerifying(false);
          reportVeriffError("Failed to open Veriff frame", error);
        }
      })
      .catch((error) => {
        setStepsPercent(5);
        setIsVerifying(false);
        reportVeriffError("Failed to fetch Veriff redirect URL", error);
      });
  };

  return (
    <div className={styles.proofOfIdentityInit}>
      <h2>Proof of Identity</h2>
      {isVerifying ? (
        <LoadingIndicator className={styles.loading} />
      ) : (
        <>
          <p>We verify your identity to meet regulatory requirements and make your account investment-ready.</p>
          <div className={styles.rule}>
            <h3>{rule.title}</h3>
            <p>{rule.description}</p>
          </div>
          {essentials.map((props) => (
            <Essential {...props} key={props.title} />
          ))}
          <p>Keep in mind when scanning your document</p>
          <Row className={styles.documentAlignments}>
            {alignments.map((props) => (
              <DocumentAlignment {...props} key={props.title} />
            ))}
          </Row>
          <Alert
            message='Be Appropriately Dressed'
            description='Please be advised that your camera is about to capture an image of you. Please be APPROPRIATELY DRESSED and be aware of your surroundings. Submitting inappropriate images may result in being blocked from the service and/or result in the suspension of your account.'
            type='warning'
          />
          <SubmitButton onClick={handleVeriffClick}>Continue with Veriff</SubmitButton>
        </>
      )}
    </div>
  );
};
